/**
 * Axios API client with interceptors for auth and error handling
 *
 * Uses lazy initialization to avoid circular dependency issues with bundlers.
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// Runtime configuration loaded from /config.js
declare global {
  interface Window {
    __NEOKANBAN_CONFIG__?: {
      API_URL?: string;
      WS_URL?: string;
    };
    __NEOKANBAN_AUTH_STORE__?: any;
  }
}

// Lazy-initialized client
let _apiClient: AxiosInstance | null = null;
let _interceptorsSetup = false;

// Get auth store from global (set by authStore.ts)
const getAuthStore = () => window.__NEOKANBAN_AUTH_STORE__;

// Single-flight refresh lock: concurrent 401s share one /auth/refresh call so the
// backend's refresh-token rotation/blocklist doesn't log the user out at random.
let refreshPromise: Promise<string> | null = null;

/**
 * Perform a token refresh, returning the new access token.
 * Uses a bare axios call (bypassing the interceptor) with an explicit timeout so a
 * hung refresh endpoint doesn't stall every failed request forever.
 */
async function performTokenRefresh(refreshToken: string): Promise<string> {
  const config = window.__NEOKANBAN_CONFIG__ || {};
  const API_URL = config.API_URL ?? '';
  const API_PREFIX = '/api/v1';

  const response = await axios.post(
    `${API_URL}${API_PREFIX}/auth/refresh`,
    { refreshToken },
    { timeout: 15000 }
  );

  const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

  const authStore = getAuthStore();
  authStore?.getState().setTokens(newAccessToken, newRefreshToken);

  return newAccessToken;
}

/**
 * Refresh the access token out-of-band, sharing the single-flight refresh lock
 * used by the axios interceptor. Streaming endpoints (fetch-based, bypassing the
 * interceptor) call this to recover from an expired access token without racing
 * the interceptor's own refresh. Returns the new access token, or null if there
 * is no refresh token or the refresh failed.
 */
export async function refreshAccessToken(): Promise<string | null> {
  const authStore = getAuthStore();
  if (!authStore) return null;

  const { refreshToken } = authStore.getState();
  if (!refreshToken) return null;

  try {
    if (!refreshPromise) {
      refreshPromise = performTokenRefresh(refreshToken).finally(() => {
        refreshPromise = null;
      });
    }
    return await refreshPromise;
  } catch {
    return null;
  }
}

/**
 * Redirect to the login page, preserving the current location so the user can be
 * returned to it after re-authenticating.
 */
function redirectToLogin(): void {
  if (window.location.pathname === '/login') {
    window.location.href = '/login';
    return;
  }
  const current = window.location.pathname + window.location.search;
  window.location.href = `/login?redirect=${encodeURIComponent(current)}`;
}

/**
 * Get the API client instance (lazy initialization)
 */
function getApiClient(): AxiosInstance {
  if (!_apiClient) {
    const config = window.__NEOKANBAN_CONFIG__ || {};
    const API_URL = config.API_URL ?? '';
    const API_PREFIX = '/api/v1';

    _apiClient = axios.create({
      baseURL: `${API_URL}${API_PREFIX}`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Setup interceptors once
  if (!_interceptorsSetup && _apiClient) {
    _interceptorsSetup = true;

    // Request interceptor - add auth token
    _apiClient.interceptors.request.use(
      (reqConfig: InternalAxiosRequestConfig) => {
        const authStore = getAuthStore();
        if (authStore) {
          const { accessToken, currentOrgId } = authStore.getState();

          if (accessToken) {
            reqConfig.headers.Authorization = `Bearer ${accessToken}`;
          }

          if (currentOrgId) {
            reqConfig.headers['X-Organization-ID'] = currentOrgId;
          }
        }

        return reqConfig;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors and token refresh
    _apiClient.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        // Auth endpoints (login, refresh, verify, oauth...) return 401 for genuine
        // credential failures, not expired sessions - never trigger refresh/logout
        // for them, so the real error message propagates to the caller.
        const isAuthEndpoint = originalRequest?.url?.startsWith('/auth/') ?? false;

        // Handle 401 errors - attempt token refresh
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;

          const authStore = getAuthStore();
          if (authStore) {
            const { refreshToken, logout } = authStore.getState();

            if (refreshToken) {
              try {
                // Share a single in-flight refresh across all concurrent 401s.
                if (!refreshPromise) {
                  refreshPromise = performTokenRefresh(refreshToken).finally(() => {
                    refreshPromise = null;
                  });
                }

                const newAccessToken = await refreshPromise;

                // Retry the original request with the new access token
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return _apiClient!(originalRequest);
              } catch {
                // Refresh failed (or timed out) - logout
                logout();
                redirectToLogin();
              }
            } else {
              logout();
              redirectToLogin();
            }
          }
        }

        // Format error response as a real Error subclass so `error instanceof Error`
        // checks throughout the app succeed and backend messages reach the user.
        const data = error.response?.data as
          | {
              error?: { code?: string; message?: string; details?: Record<string, unknown> };
              detail?: string;
            }
          | undefined;

        const apiError = new ApiError(
          error.response?.status || 500,
          data?.error?.code || 'UNKNOWN_ERROR',
          data?.error?.message ||
            (typeof data?.detail === 'string' ? data.detail : undefined) ||
            error.message ||
            'An unexpected error occurred',
          data?.error?.details
        );

        return Promise.reject(apiError);
      }
    );
  }

  return _apiClient;
}

// Export a proxy that lazily creates the client
export const apiClient = new Proxy({} as AxiosInstance, {
  get(_target, prop: string) {
    const client = getApiClient();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  },
});

// Typed API error - a real Error subclass so `error instanceof Error` checks
// (used throughout the stores) succeed and `.message` carries the backend message.
export class ApiError extends Error {
  status: number;
  code: string;
  details?: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    // Restore prototype chain (needed when targeting ES5) so instanceof works.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// Helper to check if error is ApiError
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
